import { useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

const useAuthData = () => {
  const [userData, setUserData] = useState(null);
  const [userPermissions, setUserPermissions] = useState({
    isAdmin: false,
    isGerente: false,
    isUsuario: false,
    isTerceiro: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadUserData = () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        
        if (!token) {
          throw new Error('Token não encontrado');
        }

        // Decodifica o token
        const decoded = jwtDecode(token);

        // Validação dos campos obrigatórios
        if (!decoded.usuario_id || !decoded.pessoa_id || !decoded.nome || !decoded.tipo_usuario) {
          throw new Error('Token inválido: campos obrigatórios ausentes');
        }

        const userType = decoded.tipo_usuario?.toLowerCase();

        // Define as permissões baseado no tipo do usuário
        setUserPermissions({
          isAdmin: userType === 'administrador',
          isGerente: userType === 'gerenciador',
          isUsuario: userType === 'usuario',
          isTerceiro: userType === 'terceiro'
        });

        // Formata os dados para garantir a estrutura correta
        const formattedUserData = {
          usuario_id: decoded.usuario_id,
          pessoa_id: decoded.pessoa_id,
          nome: decoded.nome,
          grupos_permissao: decoded.grupos_permissao,
          nome_usuario: decoded.nome_usuario,
          email: decoded.email,
          tipo_usuario: userType,
          departamento: {
            id: decoded.departamento?.id || null,
            nome: decoded.departamento?.nome || null,
            sigla: decoded.departamento?.sigla || null
          },
          horarios: {
            entrada: decoded.horarios?.entrada || null,
            saida: decoded.horarios?.saida || null
          },
          ip_acesso: decoded.ip_acesso,
          jti: decoded.jti,
          iat: decoded.iat,
          session_id: decoded.session_id,
          nonce: decoded.nonce
        };

        setUserData(formattedUserData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar dados do usuário');
        setUserData(null);
        setUserPermissions({
          isAdmin: false,
          isGerente: false,
          isUsuario: false,
          isTerceiro: false
        });
      } finally {
        setIsLoading(false);
      }
    };

    // Carrega os dados inicialmente
    loadUserData();

    // Listener para mudanças no localStorage
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        loadUserData();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Função para verificar permissões
    const hasPermission = useCallback((requiredPermission) => {
        const { isAdmin, isGerente, isUsuario, isTerceiro } = userPermissions;

        switch (requiredPermission) {
            case 'administrador':
                return isAdmin;
            case 'gerenciador':
                return isAdmin || isGerente;
            case 'usuario':
                return isAdmin || isGerente || isUsuario;
            case 'terceiro':
                return isAdmin || isTerceiro;
            default:
                return false;
        }
    }, [userPermissions]);

  // Função para obter o tipo de usuário em formato legível
  const getUserTypeDisplay = () => {
    const { isAdmin, isGerente, isUsuario, isTerceiro } = userPermissions;
    if (isAdmin) return 'administrador';
    if (isGerente) return 'gerenciador';
    if (isUsuario) return 'usuario';
    if (isTerceiro) return 'terceiro';
    return 'Não definido';
  };

  // Função para verificar se o token está próximo de expirar (considerando 8h de validade)
  const isTokenNearExpiration = () => {
    if (!userData?.iat) return false;
    const tokenDuration = 8 * 60 * 60 * 1000; // 8 horas em milissegundos
    const tokenAge = Date.now() - (userData.iat * 1000);
    const thirtyMinutesInMs = 30 * 60 * 1000;
    
    return (tokenDuration - tokenAge) < thirtyMinutesInMs;
  };

  return {
    userData,            // Dados completos do usuário do token
    userPermissions,     // Objeto com as flags de permissão
    isLoading,          // Flag indicando se está carregando
    error,              // Mensagem de erro, se houver
    hasPermission,      // Função para verificar permissões
    getUserTypeDisplay, // Função para obter o tipo do usuário em formato legível
    isTokenNearExpiration // Função para verificar proximidade da expiração
  };
};

export default useAuthData;